import { useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Users,
  Ticket,
  CheckSquare,
  Music,
  DollarSign,
  ListOrdered,
  Bot,
  Lock,
} from 'lucide-react'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Plan Richard (Phase 1 & 2 Deliverables)
   Password-protected. Same style as Master Plan, content from PHASE_1_DELIVERABLES.
   ═══════════════════════════════════════════════════════════════════════════ */

const MASTER_PLAN_PASSWORD = 'betadizee101'

const colorMap: Record<string, { border: string; bg: string; text: string; pill: string }> = {
  sky: { border: 'border-white/[0.06]', bg: 'bg-white/[0.015]', text: 'text-zinc-400', pill: 'bg-sky-500/15 text-sky-200' },
  violet: { border: 'border-white/[0.06]', bg: 'bg-white/[0.015]', text: 'text-zinc-400', pill: 'bg-violet-500/15 text-violet-200' },
  emerald: { border: 'border-white/[0.06]', bg: 'bg-white/[0.015]', text: 'text-zinc-400', pill: 'bg-emerald-500/15 text-emerald-200' },
  amber: { border: 'border-white/[0.06]', bg: 'bg-white/[0.015]', text: 'text-zinc-400', pill: 'bg-amber-500/15 text-amber-200' },
  cyan: { border: 'border-white/[0.06]', bg: 'bg-white/[0.015]', text: 'text-zinc-400', pill: 'bg-cyan-500/15 text-cyan-200' },
  rose: { border: 'border-white/[0.06]', bg: 'bg-white/[0.015]', text: 'text-zinc-400', pill: 'bg-rose-500/15 text-rose-200' },
  zinc: { border: 'border-white/[0.06]', bg: 'bg-white/[0.015]', text: 'text-zinc-400', pill: 'bg-white/[0.08] text-zinc-300' },
}

interface SectionContent {
  type: 'list'
  items: string[]
}
interface SectionContentPara {
  type: 'para'
  text: string
}
interface SectionContentTable {
  type: 'table'
  headers: string[]
  rows: string[][]
}
interface SectionContentHeading {
  type: 'heading'
  level: 1 | 2
  text: string
}
interface SectionContentSubheading {
  type: 'subheading'
  text: string
}
interface SectionContentVisual {
  type: 'visual'
  variant: 'brain' | 'brainCircuit'
}

type SectionBlock =
  | SectionContent
  | SectionContentPara
  | SectionContentTable
  | SectionContentHeading
  | SectionContentSubheading
  | SectionContentVisual

interface Section {
  id: string
  number: number
  icon: typeof Users
  title: string
  subtitle?: string
  color: string
  blocks: SectionBlock[]
}

/* Intelligence loop — rendered as plain styled text, same as rest of page */
function BrainVisual() {
  const steps = [
    { title: 'User', desc: 'Shows, tickets, actions' },
    { title: 'Data', desc: 'Events, features, history' },
    { title: 'AI', desc: 'Forecast, risk, compile', accent: true },
    { title: 'Insights', desc: 'Cards, table, alerts' },
  ]
  return (
    <div className="my-10">
      <p className="text-[11px] text-zinc-600 uppercase tracking-wider mb-5">Intelligence loop</p>
      <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
        {steps.map((s, i) => (
          <div key={s.title} className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <p className={`text-[13.5px] font-semibold leading-[1.7] ${s.accent ? 'text-[#FF2300]' : 'text-white'}`}>{s.title}</p>
              <p className="text-[13.5px] text-zinc-500 leading-[1.7]">{s.desc}</p>
            </div>
            {i < steps.length - 1 && (
              <span className="text-zinc-600 mt-[2px] flex-shrink-0 text-[13.5px]">{'\u2192'}</span>
            )}
          </div>
        ))}
      </div>
      <p className="text-[13.5px] text-zinc-600 mt-3 leading-[1.7]">
        <span className="text-zinc-500">{'\u2190'}</span> Insights feed back into user experience and data layer continuously.
      </p>
    </div>
  )
}

/* Graph + retrieval — rendered as plain styled text, same as rest of page */
function BrainCircuitVisual() {
  const relationships = [
    ['Show', 'Artist'],
    ['Show', 'Market'],
    ['Show', 'Venue'],
    ['Show', 'Promoter'],
    ['Show', 'Tour Leg'],
  ]
  return (
    <div className="my-10">
      <p className="text-[11px] text-zinc-600 uppercase tracking-wider mb-5">Graph + retrieval</p>
      <div className="grid grid-cols-[14px_1fr] gap-3">
        {relationships.map(([from, to]) => (
          <div key={to} className="contents">
            <span className="text-zinc-500 leading-[1.7] pt-[2px]">{'\u2014'}</span>
            <p className="text-[13.5px] text-zinc-400 leading-[1.7]">
              <strong className="font-semibold text-[#FF2300]">{from}</strong>
              <span className="text-zinc-600 mx-1.5">{'\u2194'}</span>
              <strong className="font-semibold text-white">{to}</strong>
            </p>
          </div>
        ))}
      </div>
      <p className="text-[13.5px] text-zinc-500 mt-4 leading-[1.7]">
        Retrieve "shows like this one" — same venue type, similar cap, similar time-of-year, nearby markets — then compile a grounded baseline and insight.
      </p>
    </div>
  )
}

const sections: Section[] = [
  {
    id: 'artist-promoter',
    number: 1,
    icon: Users,
    title: 'Do first: Artist account & Promoter preset account',
    color: 'violet',
    blocks: [
      {
        type: 'list',
        items: [
          'Add **Promoter** as a role in Invite Team Member (with Manager and Support).',
          'For Promoter role, show only **Shows** and **Team** in Section & Access Permissions; lock it so it can\'t be changed.',
          'For all invites: make **per-show filter** always on, at the top, and required (must pick shows).',
          'Remove **region filter** from the invite flow.',
          'Remove **Edit show info** option from the invite flow.',
          'When a promoter is in an artist account and has Team access, they can invite people — but only as **Manager** or **Support** (same Shows + Team scope).',
          'Promoter\'s team list only shows people **they** added, not the full account team.',
          'Allow **multiple promoters** per artist account.',
          'Add entry to team hub for promoters: **"Your dashboard" / "Promoter dashboard"** (or similar).',
          'Promoter dashboard: all their shows in one place, create shows (same Add Show flow), invite artists to shows.',
          'When promoter invites an artist to a show and artist has no Dizzy account: **create the artist account** and **add the promoter** to it automatically.',
          'When artist already has Dizzy: show request in **Team Management** (e.g. "Add [Promoter] as promoter to collaborate on these shows"); accept adds promoter to team and shows.',
          'Promoter can invite **Manager/Support** to their promoter account (same Shows + Team scope).',
          'Gate promoter dashboard with **access code** for now (e.g. "Dizzy 101"); payment later.',
        ],
      },
      { type: 'para', text: 'Full spec: see PROMOTER_ACCOUNT_DELIVERABLES.md.' },
    ],
  },
  {
    id: 'shows',
    number: 2,
    icon: Ticket,
    title: 'Shows',
    color: 'sky',
    blocks: [
      { type: 'para', text: 'This Week (priority)' },
      {
        type: 'list',
        items: [
          'Add **4 boxes on top of Shows**: adjust 3 for **At Risk**, **Sellout Status**, **Tickets to Sell**; keep **Next Up** as is.',
          '**Click to view shows** — user can click a show to view it (no delete). Put delete on the 3-dot menu for now; bulk delete later.',
          '**One-click Sold Out** — add to 3-dot menu on a show.',
          'Build **Money section** (see Money list below).',
        ],
      },
      { type: 'para', text: 'Next' },
      {
        type: 'list',
        items: [
          '**Ticket Tracking Uploader** — get it working; support different file types and plain text.',
          '**Booking Google Sheet** — integration/functionality.',
        ],
      },
    ],
  },
  {
    id: 'tasks',
    number: 3,
    icon: CheckSquare,
    title: 'Tasks',
    color: 'emerald',
    blocks: [
      { type: 'para', text: 'This Week (priority)' },
      {
        type: 'list',
        items: [
          'Add **4 boxes on top of Tasks**: **This Week**, **Overdue**, **Approvals**, **Waiting On**.',
          '**Filters in Tasks**: My Tasks vs Team Tasks, For My Approval, Sent for Approval (desktop; already on mobile).',
          '**Approvals in Tasks**: if a task is assigned, allow approval or denial. Show **Approve / Deny** on the user\'s side in the Task sheet; add an "approvals" column.',
          '**Click to view task** — click a task to open and see details (no need to go 3 dots \u2192 edit).',
          '**Mark as complete** for tasks.',
          '**Assign a task to more than one person**.',
          '**Comment / note on a task**.',
        ],
      },
    ],
  },
  {
    id: 'music',
    number: 4,
    icon: Music,
    title: 'Music',
    color: 'cyan',
    blocks: [
      { type: 'para', text: 'Next' },
      {
        type: 'list',
        items: [
          '**Streaming** — develop streaming functionality.',
          '**Writer Credits** — implement writer credits.',
        ],
      },
    ],
  },
  {
    id: 'money',
    number: 5,
    icon: DollarSign,
    title: 'Money',
    color: 'amber',
    blocks: [
      { type: 'para', text: 'This Week (priority)' },
      {
        type: 'list',
        items: [
          'Build the **Money section** — see "Money" tab in the Google Sheet for full breakdown.',
        ],
      },
    ],
  },
  {
    id: 'order',
    number: 6,
    icon: ListOrdered,
    title: 'Order at a glance',
    color: 'rose',
    blocks: [
      {
        type: 'table',
        headers: ['Order', 'Area', 'When'],
        rows: [
          ['1', 'Artist account & Promoter preset account', 'First'],
          ['2', 'Shows (4 boxes, click to view, one-click sold out, Money section)', 'This Week'],
          ['3', 'Tasks (4 boxes, filters, approvals, click to view, complete, multi-assign, comments)', 'This Week'],
          ['4', 'Money section', 'This Week'],
          ['5', 'Shows (ticket uploader, booking Google Sheet); Music (streaming, writer credits)', 'Next'],
        ],
      },
    ],
  },
  {
    id: 'phase2',
    number: 7,
    icon: Bot,
    title: 'Phase 2 \u2014 AI Intelligence',
    subtitle: 'The DIZEE Brain: modern AI architecture, shows-first, factual, future-proof.',
    color: 'zinc',
    blocks: [
      { type: 'para', text: 'How modern AI systems work in production, how each pattern fits DIZEE, and a concrete architecture: events \u2192 features \u2192 forecasting \u2192 insights \u2192 delivery.' },
      { type: 'heading', level: 1, text: 'Simple plan (AI Intelligence for DIZEE)' },
      { type: 'para', text: 'Focus on shows first. Make it feel built into how people use DIZEE, not a separate chat. Keep recommendations factual (stats and comparisons), not telling people what to do. Design so we can add external data and more personal stuff later.' },
      { type: 'subheading', text: 'Set up a clear approach' },
      { type: 'list', items: [
        'AI lives inside the product \u2014 recommendations and smarts as you go, not a chat box first.',
        'Everything we show is factual: "Based on the stats, this is what it looks like" \u2014 no guaranteed outcomes or commands.',
        'Prioritize what affects bottom line: risk, sell-out, bonuses, cannibalization, missed money.',
        'Build so we can add later: internet/social data, Bandsintown, personal/habit insights, music intelligence \u2014 without redoing the core.',
      ] },
      { type: 'subheading', text: 'Set up how the AI interacts with the user' },
      { type: 'list', items: [
        '**Primary experience: intelligence in context.** User sees AI output where they already work \u2014 show list, intelligence view, individual show pages. No separate "AI" area needed.',
        '**Dedicated intelligence view.** One place (e.g. Shows \u2192 Intelligence) where all show-related insights live in one table: at-risk, pacing, sell-out, bonuses, cannibalization, market timing.',
        '**Notifications when something needs attention.** Show at risk, on track to sell out, near a bonus, cannibalization \u2014 ping the user (in-app bell, badge, or email). One notification system for all triggers.',
        '**Tone: factual, not prescriptive.** Every message presents data and comparisons. We do not tell them to "stop" or "do X" \u2014 we show what the stats say.',
        '**No guaranteed outcomes.** We say "on track to sell out" or "at risk of missing the bonus" \u2014 always what the data suggests, not a promise.',
        '**Optional in-context nudges.** Short line or card with the most relevant insight per show (pacing vs last time, near bonus, at risk).',
        '**Chat later, as an addition.** Main value is embedded intelligence and notifications; chat is not the first way users interact with the AI.',
      ] },
      { type: 'subheading', text: 'Set up at-risk shows (build on what exists)' },
      { type: 'list', items: [
        'Add preset parameters we define and custom parameters the user can set.',
        'When a show is at risk, notify the user outside the show section (ping/alert).',
        'Use history: compare to last time in that market (e.g. "Last time you played LA at 14 days out you had 60 sold; this show has 1,443" or the opposite for high risk).',
      ] },
      { type: 'subheading', text: 'Set up pacing and sell-out prediction' },
      { type: 'list', items: [
        'Use every ticket count update (daily/weekly) as a data point; compute pace between counts over the life of the show.',
        'Store pacing and counts so we can use them as history across shows.',
        'For a show (e.g. LA, 2,000 cap, 500 sold, 30 days out), find the same artist\'s last show in that market and compare: same "days out," how many sold and how fast.',
        'Show: "Your [market] show is pacing faster/slower than your last [market] show at this date."',
        'When on pace to sell out, notify the user (present as info, not a command). When pacing looks weak, surface factual message \u2014 still not a directive. (Later) Optional: graph of ticket counts and pacing.',
      ] },
      { type: 'subheading', text: 'Set up bonus and money-left-on-the-table tracking' },
      { type: 'list', items: [
        'Support guarantee + bonuses tied to ticket thresholds (e.g. $2,000 at 1,000 tickets).',
        'When close to a bonus tier (e.g. 990 sold, bonus at 1,000), alert: "Current count 990; $2,000 bonus at 1,000 tickets \u2014 10 more to avoid leaving money on the table."',
        'Track which bonuses were missed and total dollar amount missed per show and over time. Show user: how many bonuses missed and how much more they could have made.',
      ] },
      { type: 'subheading', text: 'Set up the intelligence view (one place for all show intelligence)' },
      { type: 'list', items: [
        'Dedicated view for shows \u2014 analytics or intelligence view \u2014 same show list, different lens.',
        'Spreadsheet-style table with columns: at-risk (and why), pacing vs last time in market, sell-out on-track flag, near-bonus / missed-bonus flags and amounts.',
        'Feed all the logic into this view; start with a subset of columns and add more (cannibalization, market timing) over time.',
      ] },
      { type: 'subheading', text: 'Set up cannibalization detection' },
      { type: 'list', items: [
        'Use show metadata: market/city, state, date. Detect when two shows are too close in geography and time (e.g. LA + OC within 3 weeks).',
        'Make the rule configurable (distance + time window).',
        'In intelligence view or via notification: "You have two shows in very close markets soon; they may hurt each other. Consider spacing them out." \u2014 factual only. (Later) Other big events in the area as extra context.',
      ] },
      { type: 'subheading', text: 'Set up late vs early market behavior' },
      { type: 'list', items: [
        'Use historical ticket counts by market (and optionally by artist) to label markets as "early" or "late". Store that behavior per market (and optionally per artist).',
        'In intelligence view or tooltips: "This market tends to buy late" or "tends to buy in advance." When a show in a late market has low sales with time left: "Sales look low but this market typically buys closer to the show date." \u2014 empirical only.',
      ] },
      { type: 'subheading', text: 'Set up promoter intelligence' },
      { type: 'list', items: [
        'Let promoters upload ticket data (e.g. from Ticketmaster) into DIZEE. Give them the same intelligence (at-risk, pacing, sell-out, bonuses, cannibalization, late/early markets) in a promoter dashboard.',
        'Rank their artists by total ticket sales and by velocity. Surface "Top 5 selling artists" by volume or speed. Position: "Intelligence dashboard for your ticket counts."',
      ] },
      { type: 'subheading', text: 'Set up for later (Phase 2)' },
      { type: 'list', items: [
        'Location and market analytics; external data (social, Bandsintown); publish shows to Bandsintown when feasible; music-focused intelligence; personal/habit layer \u2014 architect so we can add "knows you" / context-aware features later.',
      ] },
      { type: 'para', text: '**Quick reference \u2014 what to build when.** Priority 1: How the AI interacts (in-context + intelligence view + notifications + factual tone). At-risk, pacing, sell-out, bonuses, intelligence view, cannibalization, late/early markets, promoter dashboard. Priority 2: Location/market analytics, external data, Bandsintown publish, music intelligence, personal/habit architecture only.' },
      { type: 'visual', variant: 'brain' },
      { type: 'heading', level: 1, text: '1) The modern \u201cAI brain\u201d pattern (what actually works in production)' },
      { type: 'para', text: 'Strong AI systems = **multiple cooperating modules**, not one model.' },
      { type: 'subheading', text: 'A. Perception layer (raw inputs \u2192 signals)' },
      { type: 'list', items: [
        '**Event stream ingestion:** ticket counts, on-sale date, cap, guarantee/bonus tiers, promoter updates, notes (later: ad spend, etc.).',
        '**Normalization + entity resolution:** "LA" vs "Los Angeles", venues, recurring promoters, tour legs, timezone handling.',
        '**Feature extraction:** days-out, velocity, acceleration, percentile rank vs history, market seasonality, distance between shows (cannibalization), bonus proximity.',
        '**Fit to DIZEE:** Clean event model + normalization rules \u2192 everything else composable.',
      ] },
      { type: 'subheading', text: 'B. Memory layer (short-term + long-term)' },
      { type: 'list', items: [
        '**Hot / short-term:** recent ticket updates, last computed risk state, last notification sent.',
        '**Cold / long-term:** prior shows, market behavior profiles, artist-by-market baselines, promoter reliability.',
        '**External memory (retrieval):** pull the right slice of historical shows / similar markets / venues at runtime (RAG on your own structured data).',
        '**Fit to DIZEE:** "Compare to last time in market at this days-out" = retrieval query over structured history.',
      ] },
      { type: 'subheading', text: 'C. World model (predictive + causal)' },
      { type: 'list', items: [
        '**Forecasting models:** future ticket trajectory given current trajectory + known patterns.',
        '**Uncertainty estimation:** confidence bands, not a single number.',
        '**Regime detection:** "late-buying market" vs "early market", "momentum shift" after announcement.',
        '**Fit to DIZEE:** Pacing/sell-out "on track" flags; "at risk" classification; market behavior tagging (late vs early).',
      ] },
      { type: 'subheading', text: 'D. Planner / Orchestrator (agentic, not chatty)' },
      { type: 'list', items: [
        'Decide which tools/queries to run (DB, forecasting, similarity search, cannibalization, bonus tier eval).',
        'Assemble results into a compact **structured insight object**.',
        'Trigger notification workflows.',
        '**Fit to DIZEE:** Silent orchestrator that continuously computes **show intelligence objects** \u2192 in-context cards, intelligence view table, notification triggers.',
      ] },
      { type: 'subheading', text: 'E. Verifier / Critic (factual, no nonsense)' },
      { type: 'list', items: [
        '**Grounding:** every claim traces to data or a computed metric.',
        '**Consistency checks:** reject if "20% slower" but math doesn\u2019t match.',
        '**Uncertainty + calibrated language:** "on track / at risk" with probabilities or bands.',
        '**Schema enforcement:** strict JSON outputs so UI is deterministic and testable.',
        '**Fit to DIZEE:** Enforces **factual, stats-and-comparisons, no bossy instructions.**',
      ] },
      { type: 'visual', variant: 'brainCircuit' },
      { type: 'heading', level: 1, text: '2) The \u201cDIZEE Brain\u201d \u2014 concrete architecture (shows-first)' },
      { type: 'subheading', text: 'Layer 1 \u2014 Event-sourced show ledger' },
      { type: 'list', items: [
        'Store everything as **events**, not overwriting: TicketCountUpdated(show_id, source, count, timestamp), BonusTierDefined, ShowScheduled, GuaranteeDefined, ShowEdited; later AdSpendUpdated, PostAnnounce, etc.',
        'Enables history, pacing, and "what changed" without guesswork.',
      ] },
      { type: 'subheading', text: 'Layer 2 \u2014 Feature store + derived metrics' },
      { type: 'list', items: [
        'Nightly + on-event: days_out, sales_velocity_7d/3d, acceleration, sell_through, pace_index vs baseline, bonus_distance, cannibalization_score, market_behavior_profile.',
        'Stable "facts layer" the UI can trust.',
      ] },
      { type: 'subheading', text: 'Layer 3 \u2014 Forecasting + risk models' },
      { type: 'list', items: [
        '**Deterministic rules:** bonus proximity alerts, cannibalization thresholds, "pacing slower than last time at same days-out".',
        '**Probabilistic models:** sell-out probability, expected final tickets, P(miss bonus), P(miss target).',
        'Start with gradient boosting + handcrafted features; graduate to time-series transformers / TS foundation models as data grows.',
      ] },
      { type: 'subheading', text: 'Layer 4 \u2014 Insight compiler (structured \u201ctruth objects\u201d)' },
      { type: 'list', items: [
        'Per show: insight_type (PACING_SLOWDOWN, NEAR_BONUS, CANNIBALIZATION, LATE_MARKET_CONTEXT, RISK_SPIKE), evidence (exact numbers + comparisons), confidence, data_refs, copy (1\u20132 sentences, factual).',
        'LLM only as **narrative renderer** with schema + cited numbers \u2014 never invent.',
      ] },
      { type: 'subheading', text: 'Layer 5 \u2014 Delivery (embedded UX, not chat)' },
      { type: 'list', items: [
        'In-context cards on show list + show detail.',
        'Intelligence view table powered by insight objects.',
        'Notification engine: near bonus, risk spike, sellout on-track change, cannibalization detected \u2014 with triggers, cooldowns, dedupe.',
      ] },
      { type: 'heading', level: 2, text: '3) Cutting-edge concepts that make DIZEE feel \u201c10x smarter\u201d' },
      { type: 'list', items: [
        '**Graph + retrieval:** Artist \u2194 Market \u2194 Venue \u2194 Promoter \u2194 Tour Leg; "shows like this one" = relationship-aware selection (Graph-RAG style).',
        '**Change-point + anomaly detection:** "Pace shifted upward in last 48h"; "deviated from typical late-market curve."',
        '**Uncertainty as UI primitive:** probability bands, scenario ranges \u2014 stay factual, no guarantees.',
        '**Self-evaluation loops:** backfill results after show date; score forecast error and notification precision; recalibrate per artist tier.',
        '**Tool-using micro-agents:** risk agent, bonus agent, cannibalization agent, market-profile agent, forecast agent \u2014 orchestration merges outputs.',
      ] },
      { type: 'visual', variant: 'brain' },
      { type: 'heading', level: 1, text: '4) Richard brief \u2014 implementation guidance (one page)' },
      { type: 'list', items: [
        '**Event model first** \u2014 immutable ticket updates + config events.',
        '**Derived metrics layer** \u2014 feature store + cached show snapshots.',
        '**Insight objects as the contract** between backend and UI.',
        '**Notification engine** \u2014 insight triggers + cooldown + dedupe.',
        '**LLM only as a renderer** \u2014 structured output, no free-form claims.',
        '**Forecasting engine** \u2014 modular so you can swap baseline \u2192 TS foundation model later.',
        '**Evaluation loop** \u2014 backtest every show once it completes.',
        'That stack = "AI brain" credibility without turning the product into a chat toy.',
      ] },
      { type: 'heading', level: 2, text: 'Backend object schema (drop into DIZEE DB design)' },
      { type: 'para', text: '**Events (immutable)**' },
      { type: 'list', items: [
        'TicketCountUpdated(show_id, source, count, timestamp)',
        'BonusTierDefined(show_id, threshold, amount)',
        'ShowScheduled(show_id, city, venue, capacity, date, on_sale_date)',
        'GuaranteeDefined(show_id, amount)',
        'ShowEdited(show_id, field, old, new, timestamp)',
      ] },
      { type: 'para', text: '**Snapshots (cached show state)**' },
      { type: 'list', items: [
        'show_id, computed_at, days_out, sold, cap, sell_through, velocity_7d, velocity_3d, acceleration, pace_index, bonus_distance, cannibalization_score, market_behavior_profile.',
      ] },
      { type: 'para', text: '**Insight objects (machine-generated, schema-enforced)**' },
      { type: 'list', items: [
        'show_id, insight_type, evidence (JSON), confidence, data_refs (event/snapshot refs), copy (1\u20132 sentences), created_at.',
      ] },
      { type: 'para', text: '**Notifications (trigger log)**' },
      { type: 'list', items: [
        'id, user_id, show_id, trigger_type (near_bonus | risk_spike | sellout_on_track | cannibalization), insight_id, sent_at, cooldown_until.',
      ] },
    ],
  },
]

function renderInlineBold(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  )
}

export default function PlanRichardPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === MASTER_PLAN_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const expandAll = () => setOpenSections(new Set(sections.map((s) => s.id)))
  const collapseAll = () => setOpenSections(new Set())

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-10" style={{ backgroundColor: '#13120A' }}>
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
              <span className="text-white text-lg font-semibold tracking-tight">DIZEE</span>
              <span className="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider bg-white/10 text-zinc-400 rounded">Plan Richard</span>
            </div>
            <p className="text-[13px] text-zinc-500">Enter the password to view Phase 1 & 2 deliverables.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-[12px] text-zinc-400 mb-2 tracking-wide uppercase">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Enter password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/40 focus:border-transparent transition-all"
              />
              {error && (
                <p className="mt-2 text-[11px] text-rose-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-6 py-3 text-sm font-medium text-zinc-950 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/30 transition-all"
            >
              View Plan
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#13120A' }}>
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.05),transparent_45%),radial-gradient(circle_at_70%_80%,rgba(255,35,0,0.06),transparent_55%)]" />
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-xl border-b border-white/[0.06]" style={{ backgroundColor: 'rgba(19,18,10,0.82)' }}>
        <div className="max-w-6xl mx-auto px-8 sm:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
            <span className="text-white text-base font-semibold tracking-tight">DIZEE</span>
            <span className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-white/[0.08] text-zinc-300 rounded-full">
              Plan Richard
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-zinc-600 uppercase tracking-wider">Confidential</span>
            <Lock className="w-3.5 h-3.5 text-zinc-600" />
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-8 sm:px-10 py-16 space-y-16">
        <div className="mb-2">
          <h1 className="text-xl font-semibold text-white">Phase 1 & Phase 2 — Simple Deliverables</h1>
          <p className="text-[13px] text-zinc-500 mt-1 italic">
            Phase 1: Dizee Beta – Final Dev Week. Do in this order. Bolded / "This Week" items matter most. Phase 2: AI Intelligence (after Phase 1).
          </p>
        </div>

        <nav className="flex flex-wrap gap-2.5">
          {sections.map((s) => {
            const c = colorMap[s.color] || colorMap.sky
            return (
              <button
                key={s.id}
                onClick={() => {
                  if (!openSections.has(s.id)) toggleSection(s.id)
                  document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                className={`px-3.5 py-2 rounded-full text-[11px] font-medium border transition-all hover:bg-white/[0.03] ${c.border} bg-transparent text-zinc-400`}
              >
                {s.number}. {s.title.replace(/^Do first: /, '').replace(/ —.*$/, '')}
              </button>
            )
          })}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={expandAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Expand All
          </button>
          <span className="text-zinc-700">|</span>
          <button
            onClick={collapseAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Collapse All
          </button>
        </div>

        <div className="space-y-6">
          {sections.map((section) => {
            const isOpen = openSections.has(section.id)
            const c = colorMap[section.color] || colorMap.sky
            const Icon = section.icon

            return (
              <div
                key={section.id}
                id={`section-${section.id}`}
                className={`rounded-2xl border ${c.border} ${c.bg} overflow-hidden transition-all scroll-mt-24`}
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-4 px-8 py-7 text-left group"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${c.pill}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] text-zinc-600 font-medium">#{section.number}</span>
                      <h2 className="text-[15px] font-semibold text-white truncate">{section.title}</h2>
                    </div>
                    {section.subtitle && (
                      <p className="text-[12px] text-zinc-500 mt-1 truncate">{section.subtitle}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-zinc-600 group-hover:text-zinc-400 transition-colors">
                    {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-8 pb-10 space-y-7">
                    {section.blocks.map((block, bi) => {
                      if (block.type === 'para') {
                        return (
                          <p key={bi} className="text-[13.5px] text-zinc-400 font-medium leading-relaxed">
                            {renderInlineBold(block.text)}
                          </p>
                        )
                      }
                      if (block.type === 'heading') {
                        const Tag = block.level === 1 ? 'h3' : 'h4'
                        return (
                          <Tag
                            key={bi}
                            className={block.level === 1 ? 'text-[16px] font-semibold text-white mt-8 first:mt-0 tracking-tight' : 'text-[14px] font-semibold text-zinc-300 mt-6'}
                          >
                            {block.text}
                          </Tag>
                        )
                      }
                      if (block.type === 'subheading') {
                        return (
                          <p key={bi} className="text-[13px] font-semibold text-zinc-300 mt-6 tracking-tight">
                            {block.text}
                          </p>
                        )
                      }
                      if (block.type === 'visual' && block.variant === 'brain') {
                        return <BrainVisual key={bi} />
                      }
                      if (block.type === 'visual' && block.variant === 'brainCircuit') {
                        return <BrainCircuitVisual key={bi} />
                      }
                      if (block.type === 'list') {
                        return (
                          <ul key={bi} className="space-y-3 list-none">
                            {block.items.map((item, i) => (
                              <li key={i} className="grid grid-cols-[14px_1fr] gap-3">
                                <span className="text-zinc-500 leading-[1.7] pt-[2px]">{'\u2022'}</span>
                                <span className="text-[13.5px] text-zinc-400 leading-[1.7]">
                                  {renderInlineBold(item)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )
                      }
                      if (block.type === 'table') {
                        return (
                          <div key={bi} className="overflow-x-auto rounded-lg border border-white/[0.06]">
                            <table className="w-full text-left text-[12px]">
                              <thead>
                                <tr className="border-b border-white/[0.06]">
                                  {block.headers.map((h, i) => (
                                    <th key={i} className="px-4 py-3 text-zinc-500 font-semibold uppercase tracking-wider">
                                      {h}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {block.rows.map((row, ri) => (
                                  <tr key={ri} className="border-b border-white/[0.04] last:border-0">
                                    {row.map((cell, ci) => (
                                      <td key={ci} className="px-4 py-3 text-zinc-400">
                                        {cell}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )
                      }
                      return null
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <footer className="pt-8 border-t border-white/[0.06] flex items-center justify-between">
          <span className="text-[11px] text-zinc-600">Confidential — Internal Use Only</span>
          <span className="text-[11px] text-zinc-700 italic">Source: Dizee Beta – Final Dev Week for Phase 1 (sheet).</span>
        </footer>
      </main>
    </div>
  )
}
